/**
 * SDD Detail Page
 *
 * View and edit a specific SDD with tabbed interface
 */

import * as React from 'react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import {
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@forgenetwork/ui';
import { SDDViewer, SDDEditor } from '@/components/sdd';
import { useSDD } from '@/hooks/useSDD';
import { useToast } from '@/shared/hooks/useToast';

// Search params type
type SDDSearchParams = {
  tab?: 'view' | 'edit';
};

export const Route = createFileRoute('/_authenticated/repositories/$repoId/sdd/$sddId')({
  component: SDDDetailPage,
  validateSearch: (search: Record<string, unknown>): SDDSearchParams => {
    return {
      tab: (search.tab as 'view' | 'edit') ?? 'view',
    };
  },
});

function SDDDetailPage(): React.JSX.Element {
  const { repoId, sddId } = Route.useParams();
  const search = useSearch({ from: '/_authenticated/repositories/$repoId/sdd/$sddId' });
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: sdd } = useSDD(repoId, sddId);

  const [activeTab, setActiveTab] = React.useState<'view' | 'edit'>(search.tab ?? 'view');

  // Update URL when tab changes
  const handleTabChange = (value: string): void => {
    const tab = value as 'view' | 'edit';
    setActiveTab(tab);
    navigate({
      to: '/repositories/$repoId/sdd/$sddId',
      params: { repoId, sddId },
      search: { tab },
      replace: true,
    });
  };

  const handleExport = (): void => {
    toast({
      title: 'Export SDD',
      description: 'Use the export dropdown in the viewer to download your SDD.',
    });
  };

  const handleSave = (): void => {
    toast({
      title: 'SDD Saved',
      description: 'Your changes have been saved successfully.',
    });
    // Switch back to view mode after save
    handleTabChange('view');
  };

  const handleCancel = (): void => {
    handleTabChange('view');
  };

  const handleBack = (): void => {
    navigate({
      to: '/repositories/$repoId/sdd',
      params: { repoId },
    });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                {sdd?.title ?? 'System Design Document'}
              </h1>
              <p className="text-muted-foreground mt-1">
                View and edit SDD details
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="view">View</TabsTrigger>
            <TabsTrigger value="edit">Edit</TabsTrigger>
          </TabsList>

          <TabsContent value="view" className="mt-6">
            <SDDViewer repoId={repoId} sddId={sddId} onExport={handleExport} />
          </TabsContent>

          <TabsContent value="edit" className="mt-6">
            <SDDEditor
              repoId={repoId}
              sddId={sddId}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
